import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, Flame, Beef } from 'lucide-react';
import { useComprehensiveNutrition } from '@/contexts/NutritionContext';
import { format, subDays } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';

type TimeRange = 'weekly' | 'monthly';
type MetricType = 'calories' | 'protein';

interface ChartDataPoint {
  date: string;
  displayDate: string;
  value: number;
  target: number;
}

export const NutritionTrendsChart = () => {
  const nutritionHookData = useComprehensiveNutrition();
  const [timeRange, setTimeRange] = useState<TimeRange>('weekly');
  const [metricType, setMetricType] = useState<MetricType>('calories');
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const nutritionGoals = nutritionHookData?.nutritionGoals;
  const getNutritionTotalsRange = nutritionHookData?.getNutritionTotalsRange;

  useEffect(() => {
    const fetchData = async () => {
      if (!getNutritionTotalsRange) return;
      
      setLoading(true);
      try {
        const days = timeRange === 'weekly' ? 7 : 30;
        const data = await getNutritionTotalsRange(days);
        
        const target = metricType === 'calories' 
          ? (nutritionGoals?.daily_calories || 2000)
          : (nutritionGoals?.daily_protein || 120);

        const formattedData: ChartDataPoint[] = data.map((day: any) => ({
          date: day.date,
          displayDate: timeRange === 'weekly' 
            ? format(new Date(day.date), 'EEE')
            : format(new Date(day.date), 'MMM d'),
          value: metricType === 'calories' ? day.totals.calories : day.totals.protein,
          target
        }));

        setChartData(formattedData);
      } catch (error) {
        console.error('Error fetching nutrition trends:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, metricType, getNutritionTotalsRange, nutritionGoals]);

  const target = metricType === 'calories' 
    ? (nutritionGoals?.daily_calories || 2000)
    : (nutritionGoals?.daily_protein || 120);

  const unit = metricType === 'calories' ? 'cal' : 'g';
  const gradientId = metricType === 'calories' ? 'caloriesGradient' : 'proteinGradient';
  const primaryColor = metricType === 'calories' ? 'hsl(24, 95%, 53%)' : 'hsl(0, 84%, 60%)';
  const targetColor = 'hsl(142, 76%, 36%)';

  // Calculate average for the period
  const average = chartData.length > 0 
    ? Math.round(chartData.reduce((sum, d) => sum + d.value, 0) / chartData.length)
    : 0;

  if (!nutritionHookData) {
    return null;
  }

  return (
    <Card className="mt-6 border-0">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-2">
          <CardTitle className="flex items-center gap-2 font-normal text-base">
            <TrendingUp className="h-5 w-5 text-primary" />
            Daily {metricType === 'calories' ? 'Calories' : 'Protein'} Trends
          </CardTitle>
          
          {/* All Toggles on Same Row */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {/* Metric Type Toggle */}
            <div className="flex rounded-md p-0.5 bg-muted/50">
              <Button
                variant={metricType === 'calories' ? 'default' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => setMetricType('calories')}
              >
                <Flame className="h-3 w-3 mr-1" />
                Cal
              </Button>
              <Button
                variant={metricType === 'protein' ? 'default' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => setMetricType('protein')}
              >
                <Beef className="h-3 w-3 mr-1" />
                Prot
              </Button>
            </div>

            {/* Time Range Toggle */}
            <div className="flex rounded-md p-0.5 bg-muted/50">
              <Button
                variant={timeRange === 'weekly' ? 'default' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => setTimeRange('weekly')}
              >
                Weekly
              </Button>
              <Button
                variant={timeRange === 'monthly' ? 'default' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => setTimeRange('monthly')}
              >
                Monthly
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {/* Stats Summary */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: primaryColor }}
            />
            <span className="text-muted-foreground">Avg:</span>
            <span className="font-medium">{average} {unit}</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: targetColor }}
            />
            <span className="text-muted-foreground">Target:</span>
            <span className="font-medium">{target} {unit}</span>
          </div>
        </div>

        {/* Chart */}
        <div className="h-[200px] sm:h-[250px]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Loading chart data...
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              No nutrition data for this period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={chartData} 
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={primaryColor} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={primaryColor} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="hsl(var(--border))" 
                  vertical={false}
                />
                <XAxis 
                  dataKey="displayDate" 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  interval={timeRange === 'monthly' ? 4 : 0}
                />
                <YAxis 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => metricType === 'calories' ? `${value}` : `${value}g`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                  formatter={(value: number) => [`${value} ${unit}`, metricType === 'calories' ? 'Calories' : 'Protein']}
                  labelFormatter={(label) => label}
                />
                <ReferenceLine 
                  y={target} 
                  stroke={targetColor}
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={primaryColor}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

      </CardContent>
    </Card>
  );
};
